import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import { Column, DataTable, Dialog, ProgressBar, SCIcon, Tag, TabView, TabPanel, FilterMatchMode, InputText } from '../../Common/Imports';
import { ApiResponse, ImportData, ImportData_EXT } from '../../Models/Classes';
import { TagSeverityType } from '../../Models/Types';
import DataImport from './DataImport';
import { useToast } from '../../Helpers/ToastHelper';
import { FormatDateTimeFromDate } from '../../Common/Utility/FormatDateTimeFromDate';

interface DataImportListTodoProps {
    onDataChanged?: () => void;
}

export const DataImportListTodo: React.FC<DataImportListTodoProps> = ({ onDataChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState<ImportData[]>([]);
    const AxiosHelper = useAxios();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [EditData, SetEditData] = useState<ImportData | null>(null);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
    const [globalFilter, setGlobalFilter] = useState<string>('');

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<ImportData[]>>(`Imports/GetListToDo`);
            if (resp.data.code === 0) {
                setData(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    // Filter data by importInfoType
    const errorData = useMemo(() => data.filter(item => item.importInfoType === 'E'), [data]);
    const warningData = useMemo(() => data.filter(item => item.importInfoType === 'W'), [data]);
    const okData = useMemo(() => data.filter(item => item.importInfoType === null || item.importInfoType === undefined), [data]);

    // Group errors by importInfo type for sub-tabs
    const errorsByType = useMemo(() => {
        const grouped: Record<string, ImportData[]> = {};
        errorData.forEach(item => {
            const key = item.importInfo || 'Unknown';
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(item);
        });
        return grouped;
    }, [errorData]);

    const errorTypes = useMemo(() => Object.keys(errorsByType).sort(), [errorsByType]);
    const [errorSubTabIndex, setErrorSubTabIndex] = useState<number>(0);

    const ErrorBody = (data: ImportData) => {
        let _Value = t("DataImport:Queued");
        let _Severity: TagSeverityType = "info";
        switch (data.importInfoType) {
            case 'W':
                _Value = t("DataImport:Warning");
                _Severity = "warning";
                break;
            case 'E':
                _Value = t("DataImport:Error");
                _Severity = "danger";
                break;
            case 'K':
                _Value = t("DataImport:Ok");
                _Severity = "success";
                break;
            case 'U':
                _Value = t("DataImport:Updated");
                _Severity = "success";
                break;
        }
        return (
            <Tag value={_Value} severity={_Severity}></Tag>
        )
    }

    const DialogHeader = (data: ImportData) => {
        let _Value = t("DataImport:Queued");
        let _Severity: TagSeverityType = "info";
        switch (data.importInfoType) {
            case 'W':
                _Value = t("DataImport:Warning");
                _Severity = "warning";
                break;
            case 'E':
                _Value = t("DataImport:Error");
                _Severity = "danger";
                break;
            case 'K':
                _Value = t("DataImport:Ok");
                _Severity = "success";
                break;
            case 'U':
                _Value = t("DataImport:Updated");
                _Severity = "success";
                break;
        }
        return (
            <div className="d-flex align-items-center">
                <Tag value={_Value} severity={_Severity} className="me-2" />
                <span>{data.idImportData}</span>
            </div>
        )
    }

    const { showToast } = useToast();

    const handleRequeue = async (data: ImportData) => {
        try {
            const resp = await AxiosHelper.get<ApiResponse<ImportData_EXT>>(`Imports/GetSingle/${data.idImportData}`);
            if (resp.data.code === 0) {
                const fullData = resp.data.data;
                // Format dates and set importInfoType/importInfo to null
                const formattedData = {
                    ...fullData,
                    checkInDate: fullData.checkInDate ? new Date(fullData.checkInDate).toISOString() : null,
                    checkOutDate: fullData.checkOutDate ? new Date(fullData.checkOutDate).toISOString() : null,
                    bookingDate: fullData.bookingDate ? new Date(fullData.bookingDate).toISOString() : null,
                    pax1BirthDate: fullData.pax1BirthDate ? new Date(fullData.pax1BirthDate).toISOString() : null,
                    pax2BirthDate: fullData.pax2BirthDate ? new Date(fullData.pax2BirthDate).toISOString() : null,
                    pax3BirthDate: fullData.pax3BirthDate ? new Date(fullData.pax3BirthDate).toISOString() : null,
                    pax4BirthDate: fullData.pax4BirthDate ? new Date(fullData.pax4BirthDate).toISOString() : null,
                    importInfoType: null,
                    importInfo: null,
                };
                
                const saveResp = await AxiosHelper.post<ApiResponse<boolean>>('Imports/SaveSingle', formattedData);
                if (saveResp.data.data) {
                    showToast("success", t("Common.Toast.Success"), t("Common.Toast.SaveSuccess"));
                    GetData();
                    onDataChanged?.();
                } else {
                    showToast("error", t("Common.Toast.Error"), saveResp.data.message);
                }
            }
        } catch {
            showToast("error", t("Common.Toast.Error"), t("Common.Toast.SaveError"));
        }
    };

    const handleDialogClose = () => {
        SetEditData(null);
        GetData();
        onDataChanged?.();
    };

    const editBody = (data: ImportData) => {
        const showRequeue = data.importInfoType === 'E' || data.importInfoType === 'W';
        return (
            <div className="d-flex gap-2">
                {showRequeue && <SCIcon Name="Requeue" Classes="text-warning" Pointer OnClick={() => handleRequeue(data)} />}
                <SCIcon Name="Edit" Classes="text-secondary" Pointer OnClick={() => SetEditData(data)} />
            </div>
        );
    }

    const DateBody = (value: Date | string | null | undefined, withTime = false) => {
        if (!value) return "";

        const parsedDate = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(parsedDate.getTime())) return "";

        return FormatDateTimeFromDate(parsedDate, { withTime });
    };

    const defaultFilters = {
        brandName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        structureName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        customerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        providerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        importInfo: { value: null, matchMode: FilterMatchMode.CONTAINS }
    };

    const renderDataTable = (tableData: ImportData[]) => (
        <div className="imports-table-section">
            <div className="imports-table-toolbar">
                <div>
                    <strong>{tableData.length.toLocaleString()} records</strong>
                    <span>Use the search to filter the visible category.</span>
                </div>
                <span className="p-input-icon-left imports-search">
                    <i className="pi pi-search" aria-hidden="true" />
                    <InputText
                        value={globalFilter}
                        onChange={(event) => setGlobalFilter(event.target.value)}
                        placeholder="Search reservations, hotels or errors"
                        aria-label="Search imports"
                    />
                </span>
            </div>
            <DataTable
            value={tableData}
            size="small"
            stripedRows
            rowHover
            paginator
            rows={25}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            rowsPerPageOptions={[25, 50, 100]}
            filterDisplay="menu"
            filters={defaultFilters}
            globalFilter={globalFilter}
            globalFilterFields={['brandName', 'structureName', 'customerReservationCode', 'providerReservationCode', 'importInfo']}
            emptyMessage="No imports match the selected filters."
            className="imports-table"
            tableStyle={{ minWidth: '72rem' }}>
            <Column
                sortField="importInfoType"
                field="importInfoType"
                header="Status"
                sortable
                body={ErrorBody}
                style={{ maxWidth: '20px!important', width: "20px" }}
                bodyClassName="table-cell-nowrap"
            />
            <Column
                field="importDate"
                header="ImportDate"
                sortable
                body={(e: ImportData) => DateBody(e.importDate, true)}
                style={{ maxWidth: '140px!important', width: "140px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="bookingDate"
                header="BookingDate"
                sortable
                body={(e: ImportData) => DateBody(e.bookingDate)}
                style={{ maxWidth: '120px!important', width: "120px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="checkInDate"
                header="CheckInDate"
                sortable
                body={(e: ImportData) => DateBody(e.checkInDate)}
                style={{ maxWidth: '120px!important', width: "120px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="brandName"
                header="Brand Name"
                sortable
                filter
                filterPlaceholder="Search..."
                style={{ maxWidth: '100px!important', width: "100px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="structureName"
                header="Structure Name"
                sortable
                filter
                filterPlaceholder="Search..."
                style={{ maxWidth: '200px!important', width: "200px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="customerReservationCode"
                header="Customer Reservation Code"
                sortable
                filter
                filterPlaceholder="Search..."
                style={{ maxWidth: '200px!important', width: "200px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="providerReservationCode"
                header="Provider Reservation Code"
                sortable
                filter
                filterPlaceholder="Search..."
                style={{ maxWidth: '200px!important', width: "200px" }}
                bodyClassName="table-cell-nowrap" />
            <Column
                field="importInfo"
                header="Info"
                sortable
                filter
                filterPlaceholder="Search..."
                body={(rowData) => {
                    const translationKey = `Imports.Errors.${rowData.importInfo}`;
                    const translated = t(translationKey);
                    const showFallback = translated === translationKey;

                    return (
                        <div className="scrollable-cell">
                            {showFallback ? rowData.importInfo : translated}
                        </div>
                    );
                }}
            />
            <Column
                field="active"
                body={(e: ImportData) => editBody(e)}
                style={{ maxWidth: '20px!important', width: "20px" }}
                bodyClassName="table-cell-nowrap" />
            </DataTable>
        </div>
    );

    return (
        TableLoader ?
            <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
            :
            <>
                {
                    EditData && EditData.idImportData &&
                    <Dialog header={DialogHeader(EditData)} visible={true} onHide={handleDialogClose}>
                        <DataImport IdImportData={EditData.idImportData} onSaved={handleDialogClose} />
                    </Dialog>
                }
                <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)} className="tabview-full-width">
                    <TabPanel header={`${t("DataImport:Error")} (${errorData.length})`}>
                        {errorTypes.length > 0 ? (
                            <TabView activeIndex={errorSubTabIndex} onTabChange={(e) => setErrorSubTabIndex(e.index)} className="tabview-nested">
                                {errorTypes.map((errorType) => {
                                    const translationKey = `Imports.Errors.${errorType}`;
                                    const translated = t(translationKey);
                                    const label = translated === translationKey ? errorType : translated;
                                    return (
                                        <TabPanel key={errorType} header={`${label} (${errorsByType[errorType].length})`}>
                                            {renderDataTable(errorsByType[errorType])}
                                        </TabPanel>
                                    );
                                })}
                            </TabView>
                        ) : (
                            renderDataTable(errorData)
                        )}
                    </TabPanel>
                    <TabPanel header={`${t("DataImport:Warning")} (${warningData.length})`}>
                        {renderDataTable(warningData)}
                    </TabPanel>
                    <TabPanel header={`${t("DataImport:Queued")} (${okData.length})`}>
                        {renderDataTable(okData)}
                    </TabPanel>
                </TabView>
            </>
    );
};

export default DataImportListTodo;
